import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'react-select';
import {
	useCreateStudioPlayListMutation,
	useGetStudioPlayListByIdQuery,
	useGetVideosListQuery,
	usePatchStudioPlayListMutation
} from '../../../service';
import { Controller, useForm } from 'react-hook-form';
import { FormFeedback, Input } from 'reactstrap';
import { userInfoAtom } from '../../../data/atoms/utilsAtoms';
import { useAtomValue } from 'jotai';
import { getVideoDropDownList } from '../../../redux/videoSlice';
import Toast from '../../../utils/toastManager';

const DUPLICATE_PLAYLIST_MESSAGE = 'Unable to add this video because it is already in this playlist.';

const getVideoId = video => video?._id?.toString?.() || video?.value?.toString?.() || video?.id?.toString?.();

const StudioPlayListForm = ({ setShow, editPlaylistID, setnModalShow }) => {
	const {
		control,
		handleSubmit,
		setValue,
		formState: { errors }
	} = useForm();
	const dispatch = useDispatch();
	const { data, isSuccess, isError } = useGetStudioPlayListByIdQuery(editPlaylistID, {
		skip: !editPlaylistID
	});
	const [reqCreate, resCreate] = useCreateStudioPlayListMutation();
	const [reqPatch, resPatch] = usePatchStudioPlayListMutation();
	const [resVideo, setResVideo] = useState([]);

	const userData = useAtomValue(userInfoAtom);
	const isSubUser = userData?.user?.userType === 'subuser';
	const customStyles = {
		multiValueRemove: base => ({
			...base,
			display: isSubUser ? 'none' : 'block' // Hide cross icon for subusers
		}),
		multiValue: base => ({
			...base,
			backgroundColor: isSubUser ? '#e0e0e0' : base.backgroundColor // Optional: Change background color for subusers
		})
	};

	const { data: videoData, isSuccess: videoSuccess, error, isLoading } = useGetVideosListQuery();
	const videoDropdownList = useSelector(state => state.videoState.videoDropdownList);
	const [name, setName] = useState('');
	const [existingVideos, setExistingVideos] = useState([]);
	const [videos, setVideos] = useState([]);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		if (isLoading) setLoading(true);
		if (error) console.log('error in get video list', error);
		if (videoSuccess) setResVideo(videoData);
	}, [isLoading, isSuccess]);

	useEffect(() => {
		if (resVideo) {
			const res1 = resVideo.map(el => ({
				...el,
				label: el.title,
				value: el._id
			}));
			dispatch(getVideoDropDownList(res1));
		}
	}, [resVideo, dispatch]);
	const [formattedVideos, setFormattedVideos] = useState([]);

	useEffect(() => {
		if (editPlaylistID) {
			if (isSuccess && data) {
				setName(data.playlistWithVideos.name);
				setExistingVideos(data.playlistWithVideos.videos);
				const formattedExistingVideos = data.playlistWithVideos.videos.map(video => ({
					label: video.title, // Display title
					value: video._id, // Unique identifier
					...video // Keep other fields if needed
				}));
				setFormattedVideos(formattedExistingVideos);

				setValue('name', data.playlistWithVideos.name); // Update React Hook Form value
				setValue(
					'videos',
					data.playlistWithVideos.videos.map(video => video._id)
				);
			}
			if (isError) {
				Toast.error('Error fetching playlist details');
			}
		}
	}, [editPlaylistID, isSuccess, isError, data]);

	// Populate video dropdown list
	useEffect(() => {
		if (videoSuccess) {
			const formattedVideos = videoData.map(video => ({
				_id: video._id,
				name: video.title
			}));
			dispatch(getVideoDropDownList(formattedVideos));
		}
	}, [videoSuccess, videoData, dispatch]);

	// Debugging videoDropdownLis

	const onSubmit = async formData => {
		console.log('Submitted Data:', { ...formData, videos });
		console.log('existing videos', existingVideos);
		try {
			const selectedVideoIds = (videos || []).map(getVideoId).filter(Boolean);
			if (new Set(selectedVideoIds).size !== selectedVideoIds.length) {
				Toast.error(DUPLICATE_PLAYLIST_MESSAGE);
				return;
			}

			if (editPlaylistID) {
				let videoIdsToAdd = [];
				let videoIdsToRemove = [];
				videoIdsToAdd =
					videos.length > 0 ? videos.filter(videoId => !existingVideos.some(video => video._id === videoId._id)) : [];
				if (!isSubUser) {
					videoIdsToRemove =
						videos.length > 0
							? existingVideos
									.map(video => video._id)
									.filter(existingId => !videos.some(formVideo => formVideo._id === existingId))
							: [];
				}
				const requestBody = {
					...formData,
					videoIdsToAdd,
					videoIdsToRemove
				};
				await reqPatch({ id: editPlaylistID, payload: requestBody }).unwrap();
				setShow(false);
				setnModalShow(false);
				Toast.success('Playlist updated successfully!');
			} else {
				console.log(videos);
				const requestBody = { ...formData, videoIdsToAdd: videos };
				await reqCreate(requestBody).unwrap();
				setShow(false);
				setnModalShow(false);
				Toast.success('Playlist created successfully!');
			}
		} catch (error) {
			Toast.error(
				error?.data?.message ||
					(editPlaylistID
						? 'Failed to update playlist. Please try again later.'
						: 'Failed to create new playlist. Please try again later.')
			);
		}
	};

	return (
		<section>
			<div className="container">
				<form onSubmit={handleSubmit(onSubmit)}>
					<div className="row g-2">
						<div className="col-md-12 form-group">
							<Input
								type="text"
								placeholder="Name"
								defaultValue={name}
								onChange={e => setValue('name', e.target.value)}
							/>
							{errors.name && <FormFeedback>{errors.name.message}</FormFeedback>}
						</div>

						{/* Videos Selection */}
						<div className="col-md-12 form-group">
							<Controller
								name="videos"
								control={control}
								rules={{
									required: 'At least one video must be selected'
								}}
								render={({ field }) => (
									<Select
										{...field}
										isMulti
										options={videoDropdownList || []}
										value={formattedVideos || []}
										isClearable={!isSubUser} // Disable the extreme right cross button for subusers
										onChange={selected => {
											setVideos(selected); // Extract `_id` for formData
											setFormattedVideos(selected); // Update the full object for UI display
											field.onChange(selected);
										}}
										placeholder="Select videos"
										styles={customStyles}
									/>
								)}
							/>
							{errors.videos && <FormFeedback>{errors.videos.message}</FormFeedback>}
						</div>
						{/* Submit Button */}
						<div className="col-md-12 form-group action__btns text-end">
							<button className="btn btn-primary" type="submit">
								{editPlaylistID ? 'Update' : 'Save'}
							</button>
						</div>
					</div>
				</form>
			</div>
		</section>
	);
};

export default StudioPlayListForm;
